import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TrackedLink } from '@components/analytics/TrackedLink';
import { trackGtmButtonClick } from '@/analytics/gtm';
import { useTheme } from '@theme/useTheme';
import { FaBars, FaTimes, FaUser, FaArrowRight } from 'react-icons/fa';
import { FaArrowRightToBracket } from 'react-icons/fa6';
import type { BackendUser } from '../../types/auth.types';

interface NavbarProps {
  isAuthenticated?: boolean;
  user?: BackendUser | null;
  onLoginClick?: () => void;
  onProfileClick?: () => void;
}

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  gtmId?: string;
  gtmSection?: string;
}

const NavLink: React.FC<NavLinkProps> = ({ to, children, className = '', onClick, gtmId, gtmSection = 'navbar_desktop' }) => {
  const { colors } = useTheme();
  const location = useLocation();
  const textRef = useRef<HTMLSpanElement>(null);
  const underlineRef = useRef<HTMLSpanElement>(null);
  const isActive = location.pathname === to;

  const updateUnderlineWidth = () => {
    if (textRef.current && underlineRef.current) {
      const textWidth = textRef.current.offsetWidth;
      underlineRef.current.style.width = `${textWidth}px`;
    }
  };

  const handleMouseEnter = () => {
    updateUnderlineWidth();
  };

  const handleMouseLeave = () => {
    if (underlineRef.current) {
      underlineRef.current.style.width = isActive ? `${textRef.current?.offsetWidth || 0}px` : '0px';
    }
  };

  // Update underline width when component mounts or when active state changes
  useEffect(() => {
    if (isActive) {
      updateUnderlineWidth();
    } else {
      if (underlineRef.current) {
        underlineRef.current.style.width = '0px';
      }
    }
  }, [isActive]);

  return (
    <TrackedLink
      to={to}
      className={`px-4 py-3 text-sm font-medium transition-all duration-300 relative ${className}`}
      style={{
        color: isActive ? colors.primary : colors.text
      }}
      {...(gtmId != null ? { gtmId, gtmSection } : {})}
      {...(typeof children === 'string' ? { gtmLabel: children } : {})}
      onClick={() => {
        onClick?.();
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span ref={textRef}>{children}</span>
      <span
        ref={underlineRef}
        className="underline absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 transition-all duration-300 ease-out"
        style={{
          backgroundColor: colors.primary,
          width: isActive ? `${textRef.current?.offsetWidth || 0}px` : '0px'
        }}
      ></span>
    </TrackedLink>
  );
};

const MobileNavLink: React.FC<NavLinkProps> = ({ to, children, className = '', onClick, gtmId, gtmSection = 'navbar_mobile' }) => {
  const { colors } = useTheme();
  const location = useLocation();
  const textRef = useRef<HTMLSpanElement>(null);
  const underlineRef = useRef<HTMLSpanElement>(null);
  const isActive = location.pathname === to;

  const updateUnderlineWidth = () => {
    if (textRef.current && underlineRef.current) {
      const textWidth = textRef.current.offsetWidth;
      underlineRef.current.style.width = `${textWidth}px`;
    }
  };

  const handleMouseEnter = () => {
    updateUnderlineWidth();
  };

  const handleMouseLeave = () => {
    if (underlineRef.current) {
      underlineRef.current.style.width = isActive ? `${textRef.current?.offsetWidth || 0}px` : '0px';
    }
  };

  // Update underline width when component mounts or when active state changes
  useEffect(() => {
    if (isActive) {
      updateUnderlineWidth();
    } else {
      if (underlineRef.current) {
        underlineRef.current.style.width = '0px';
      }
    }
  }, [isActive]);

  return (
    <TrackedLink
      to={to}
      className={`block px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg font-medium transition-all duration-300 relative text-left rounded-lg ${className}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = colors.input;
        handleMouseEnter();
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        handleMouseLeave();
      }}
      style={{
        color: isActive ? colors.primary : colors.text
      }}
      {...(gtmId != null ? { gtmId, gtmSection } : {})}
      {...(typeof children === 'string' ? { gtmLabel: children } : {})}
      onClick={() => {
        onClick?.();
      }}
    >
      <span ref={textRef}>{children}</span>
      <span
        ref={underlineRef}
        className="underline absolute bottom-2 left-3 sm:left-4 h-0.5 transition-all duration-300 ease-out"
        style={{
          backgroundColor: colors.primary,
          width: isActive ? `${textRef.current?.offsetWidth || 0}px` : '0px'
        }}
      ></span>
    </TrackedLink>
  );
};

const Navbar: React.FC<NavbarProps> = ({
  isAuthenticated = false,
  onLoginClick,
  onProfileClick
}) => {
  const { colors } = useTheme();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;

      // Prevent scrolling on both html and body
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.documentElement.style.overflow = 'unset';
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.top = 'unset';
      document.body.style.width = 'unset';
      document.body.style.height = 'unset';

      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup: restore scrolling when component unmounts
      document.documentElement.style.overflow = 'unset';
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.top = 'unset';
      document.body.style.width = 'unset';
      document.body.style.height = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'backdrop-blur-md border-b'
        : 'border-b'
        }`}
      style={{
        backgroundColor: isScrolled ? colors.background + 'F0' : colors.background,
        borderColor: colors.border,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <TrackedLink
              to="/"
              className="flex items-center"
              gtmId="nav_logo_home"
              gtmSection="navbar"
              gtmLabel="Sleep Apnea home"
            >
              <img
                src="/logo.png"
                alt="Sleep Apnea Assessment Logo"
                className="h-12 sm:h-14 md:h-16 w-auto"
              />
              <span
                className="ml-2 sm:ml-3 text-lg sm:text-xl font-bold hidden sm:block"
                style={{ color: colors.primary }}
              >
                Sleep Apnea
              </span>
            </TrackedLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-6">
              <NavLink to="/" gtmId="nav_link_home">Home</NavLink>
              <NavLink to="/about" gtmId="nav_link_about">About</NavLink>
              <NavLink to="/faq" gtmId="nav_link_faq">FAQ</NavLink>
              {isAuthenticated && <NavLink to="/news" gtmId="nav_link_news">News</NavLink>}
              {isAuthenticated && <NavLink to="/results" gtmId="nav_link_results">View Results</NavLink>}
            </div>
          </div>

          {/* Desktop Assessment & Auth Section */}
          <div className="hidden lg:block">
            <div className="ml-4 flex items-center space-x-4 md:ml-6">
              {location.pathname !== '/assessment' && (
                <TrackedLink
                  to="/assessment"
                  className="flex items-center justify-center px-4 h-10 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg cursor-pointer"
                  style={{
                    backgroundColor: colors.background,
                    color: colors.primary,
                    border: `2px solid ${colors.primary}`,
                    transition: 'all 0.3s ease-in-out'
                  }}
                  gtmId="nav_cta_get_started"
                  gtmSection="navbar_desktop"
                  gtmLabel="Get Started"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                    e.currentTarget.style.color = colors.background;
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    e.currentTarget.style.cursor = 'pointer';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.background;
                    e.currentTarget.style.color = colors.primary;
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.cursor = 'pointer';
                  }}
                >
                  Get Started
                  <FaArrowRight className="ml-2 text-sm transition-all duration-300" />
                </TrackedLink>
              )}
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    trackGtmButtonClick({
                      button_id: 'nav_auth_profile_desktop',
                      section: 'navbar_desktop',
                      label: 'Open profile',
                    });
                    onProfileClick?.();
                  }}
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-md overflow-hidden"
                  title="Open Profile"
                  style={{
                    backgroundColor: colors.secondaryButton,
                    border: `2px solid ${colors.primary}`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.error;
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <FaUser
                    size={20}
                    style={{ color: colors.primary }}
                  />
                </button>
              ) : (
                <button
                  onClick={() => {
                    trackGtmButtonClick({
                      button_id: 'nav_auth_sign_in_desktop',
                      section: 'navbar_desktop',
                      label: 'Sign In',
                    });
                    onLoginClick?.();
                  }}
                  className="flex items-center justify-center px-4 h-10 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg cursor-pointer"
                  style={{
                    backgroundColor: colors.primaryButton,
                    color: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primaryButton;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <FaArrowRightToBracket className="mr-2 text-sm" />
                  Sign In
                </button>
              )}
            </div>
          </div>

          {/* Mobile/Medium Menu Button & Auth */}
          <div className="lg:hidden flex items-center space-x-3">
            {/* Sign In Button for Mobile/Medium */}
            {!isAuthenticated && (
              <button
                onClick={() => {
                  trackGtmButtonClick({
                    button_id: 'nav_auth_sign_in_mobile',
                    section: 'navbar_mobile',
                    label: 'Sign In',
                  });
                  onLoginClick?.();
                }}
                className="flex items-center justify-center px-4 py-3 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg"
                style={{
                  backgroundColor: colors.primaryButton,
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryButton;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <FaArrowRightToBracket className="mr-2 text-sm" />
                <span className="hidden sm:inline">Sign In</span>
                <span className="sm:hidden">Sign In</span>
              </button>
            )}

            {/* Profile Button for Mobile/Medium */}
            {isAuthenticated && (
              <button
                onClick={() => {
                  trackGtmButtonClick({
                    button_id: 'nav_auth_profile_mobile',
                    section: 'navbar_mobile',
                    label: 'Open profile',
                  });
                  onProfileClick?.();
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-md overflow-hidden"
                title="Open Profile"
                style={{
                  backgroundColor: colors.secondaryButton,
                  border: `2px solid ${colors.primary}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.error;
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FaUser
                  size={20}
                  style={{ color: colors.primary }}
                />
              </button>
            )}

            {/* Hamburger Menu Button */}
            <button
              onClick={() => {
                trackGtmButtonClick({
                  button_id: 'nav_mobile_menu_toggle',
                  section: 'navbar_mobile',
                  label: isMobileMenuOpen ? 'close' : 'open',
                });
                toggleMobileMenu();
              }}
              className="inline-flex items-center justify-center p-2 rounded-md transition-colors duration-200 hover:opacity-80"
              style={{ color: colors.text }}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <FaTimes className="block h-6 w-6" />
              ) : (
                <FaBars className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(2px)'
          }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-transparent transition-opacity duration-300"></div>

          {/* Sidebar */}
          <div
            className="absolute right-0 top-0 h-full w-72 sm:w-80 max-w-sm transform transition-transform duration-300 ease-in-out translate-x-0 overflow-y-auto"
            style={{
              backgroundColor: colors.background,
              borderLeft: `1px solid ${colors.border}`,
              maxHeight: '100vh',
              overscrollBehavior: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sidebar Header */}
            <div
              className="flex items-center justify-between p-4 sm:p-6 border-b"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.background
              }}
            >
              <div className="flex items-center">
                <img
                  src="/logo.png"
                  alt="Sleep Apnea Assessment Logo"
                  className="h-8 sm:h-10 w-auto"
                />
                <span
                  className="ml-2 sm:ml-3 text-lg sm:text-xl font-bold"
                  style={{ color: colors.primary }}
                >
                  Sleep Apnea
                </span>
              </div>
              <button
                onClick={() => {
                  trackGtmButtonClick({
                    button_id: 'nav_mobile_menu_close',
                    section: 'navbar_mobile',
                    label: 'Close menu',
                  });
                  setIsMobileMenuOpen(false);
                }}
                className="p-2 rounded-full transition-all duration-200"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.input;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                style={{ color: colors.text }}
                aria-label="Close mobile menu"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            {/* Sidebar Content */}
            <div className="flex flex-col h-full">
              {/* Navigation Links */}
              <div className="flex-1 px-4 sm:px-6 py-4 sm:py-8">
                <nav className="space-y-3 sm:space-y-6">
                  <MobileNavLink to="/" gtmId="nav_mobile_link_home" onClick={() => setIsMobileMenuOpen(false)}>Home</MobileNavLink>
                  <MobileNavLink to="/about" gtmId="nav_mobile_link_about" onClick={() => setIsMobileMenuOpen(false)}>About</MobileNavLink>
                  <MobileNavLink to="/faq" gtmId="nav_mobile_link_faq" onClick={() => setIsMobileMenuOpen(false)}>FAQ</MobileNavLink>
                  {isAuthenticated && <MobileNavLink to="/news" gtmId="nav_mobile_link_news" onClick={() => setIsMobileMenuOpen(false)}>News</MobileNavLink>}
                  {isAuthenticated && <MobileNavLink to="/results" gtmId="nav_mobile_link_results" onClick={() => setIsMobileMenuOpen(false)}>View Results</MobileNavLink>}

                  {/* Get Started Button */}
                  <div className="pt-2 sm:pt-4 space-y-3">
                    <TrackedLink
                      to="/assessment"
                      className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 hover:shadow-lg cursor-pointer"
                      style={{
                        backgroundColor: colors.background,
                        color: colors.primary,
                        border: `2px solid ${colors.primary}`,
                        transition: 'all 0.3s ease-in-out'
                      }}
                      gtmId="nav_mobile_cta_get_started"
                      gtmSection="navbar_mobile"
                      gtmLabel="Get Started"
                      onClick={() => setIsMobileMenuOpen(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary;
                        e.currentTarget.style.color = colors.background;
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                        e.currentTarget.style.cursor = 'pointer';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.background;
                        e.currentTarget.style.color = colors.primary;
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.cursor = 'pointer';
                      }}
                    >
                      Get Started
                      <FaArrowRight className="ml-2 text-sm transition-all duration-300" />
                    </TrackedLink>

                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
